import React from 'react';
import {
  AreaChart,
  Area,
  Tooltip,
  ReferenceLine,
  ReferenceDot,
  Label,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AutoSizer from 'react-virtualized-auto-sizer';
import { getCashback, getFee } from '../../cashbackCalculatorUtil';
import { CashbackModel, CB_100_FEE } from '../../../utils/cashbackConstants';
import { toEuroWholeNumber, toWholeNumber } from '../../../utils/numberFormat';
import { CashbackIndicator } from './CashbackIndicator';

const Y_AXIS_WIDTH = 70;
const HIDE_CASHBACK_THRESHOLD_AT_WEALTH = 600000;

const RentabloFeeLabel = (props: any) => {
  const { viewBox, style, label } = props;

  return (
    <text
      x={viewBox.x + viewBox.width / 1.55}
      y={viewBox.y}
      fill={style.fill}
      fontWeight={style.fontWeight}
      fontSize={style.fontSize}
      textAnchor="middle"
    >
      {label}
    </text>
  );
};

const CashbackLabel = (props: any) => {
  const { viewBox, height, style, index, maxIndex, label } = props;

  if (index !== maxIndex) {
    return <></>;
  }

  const yAxisWidth = 0;
  const xAxisHeight = 30 + 20;
  const xLen = viewBox.x - yAxisWidth - 5;
  const yLen = height - xAxisHeight;
  const slope = (yLen - viewBox.y) / xLen;
  const slopeInDegrees = -(Math.atan(slope) / Math.PI) * 180;
  const yAtCenter = yLen - ((xLen - 20) / 2) * slope - 10;
  return (
    <text
      id={props.id}
      x={viewBox.x / 2 - 20}
      y={yAtCenter}
      fill={style.fill}
      fontWeight={style.fontWeight}
      fontSize={style.fontSize}
      transform={`rotate(${slopeInDegrees}, ${viewBox.x / 2}, ${yAtCenter})`}
    >
      {label}
    </text>
  );
};

const CashbackTypeLabel = (props: any) => {
  const { viewBox, height } = props;
  const { t } = useTranslation();

  const theme = useTheme();

  const xAxisHeight = 30 + 20;
  const yPos = (height - xAxisHeight) / 2;
  const cbOffset = 10;

  if (height <= 350) {
    return <></>;
  }

  return (
    <>
      <text
        x={viewBox.x - cbOffset}
        y={yPos - 7.5}
        fill={theme.palette.primary.dark}
        fontSize={14}
        transform={`rotate(-${90}, ${viewBox.x - cbOffset}, ${yPos - 7.5})`}
      >
        {`${t('cashbackCalculator.cashback.50')} ▲`}
      </text>

      <text
        x={viewBox.x + 2 * cbOffset}
        y={yPos}
        fill={theme.palette.primary.dark}
        fontSize={14}
        transform={`rotate(-${90}, ${viewBox.x + 2 * cbOffset}, ${yPos})`}
      >
        {`${t('cashbackCalculator.cashback.100')} ▼`}
      </text>
    </>
  );
};

interface ChartRecord {
  name: string;
  cashback: number;
  fee: number;
  wealth: number;
}

const buildData = (
  wealth: number,
  portfolioCommissionPercent: number,
  dataPointInterval: number,
  model: CashbackModel
) => {
  const end = Math.round(wealth / 100000 + 2) * 100000;
  const data: ChartRecord[] = [];

  for (let i = 0; i <= end; i += dataPointInterval) {
    data.push({
      name: i.toString(),
      cashback: getCashback(i * portfolioCommissionPercent, model),
      fee: getFee(i * portfolioCommissionPercent, model),
      wealth: i,
    });
  }
  return data;
};

export interface Props {
  wealth: number;
  portfolioCommissionPercent: number;
  model?: CashbackModel;
}

export const CashbackEstimationChart = ({
  wealth,
  portfolioCommissionPercent,
  model = 'flat-fee',
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));

  const cashback = getCashback(wealth * portfolioCommissionPercent, model);
  let dataPointInterval = 1000;
  if (wealth > HIDE_CASHBACK_THRESHOLD_AT_WEALTH) {
    dataPointInterval = 10000;
  }
  if (wealth > 10 * HIDE_CASHBACK_THRESHOLD_AT_WEALTH) {
    dataPointInterval = 100000;
  }

  const data = buildData(wealth, portfolioCommissionPercent, dataPointInterval, model);

  const cashback100Threshold =
    Math.ceil((2 * CB_100_FEE) / portfolioCommissionPercent / dataPointInterval) *
    dataPointInterval;

  const formatTooltip = (value: any, name: string) => {
    if (name === 'fee') {
      const val =
        value >= CB_100_FEE && model === 'flat-fee'
          ? `${toEuroWholeNumber(CB_100_FEE)} (${toEuroWholeNumber(15)} ${t(
              'cashbackCalculator.detailed.cashbackAreaChart.perMonth'
            )})`
          : toEuroWholeNumber(value);

      const label = t('cashbackCalculator.detailed.cashbackAreaChart.fee');

      return [val, label];
    }

    return [
      toEuroWholeNumber(value),
      t('cashbackCalculator.detailed.cashbackAreaChart.yourCashback'),
    ];
  };

  const formatTooltipLabel = (value: any) => {
    return `${toEuroWholeNumber(parseInt(value))} ${t(
      'cashbackCalculator.detailed.cashbackAreaChart.depotVolume'
    )}`;
  };

  const formatXAxisTick = (value: string) => {
    return `€ ${Math.round(parseInt(value) / 1000)}K `;
  };

  const formatYAxisTick = (value: string) => {
    return parseInt(value) === 0 ? '' : `€ ${toWholeNumber(Math.round(parseInt(value)))}`;
  };

  const rentabloFeeLabel = (cashback: number) => {
    if (cashback > 2 * CB_100_FEE) {
      return t(
        `cashbackCalculator.detailed.cashbackAreaChart.${
          isMobile ? 'rentabloFeeXs' : 'rentabloFee'
        }`,
        {
          fee: toEuroWholeNumber(CB_100_FEE / 12),
        }
      );
    }

    return t('cashbackCalculator.detailed.cashbackAreaChart.rentabloFeeCB50');
  };

  const xAxisTicks =
    wealth <= HIDE_CASHBACK_THRESHOLD_AT_WEALTH && model === 'flat-fee'
      ? [0, cashback100Threshold, data[data.length - 1].wealth]
      : [0, data[data.length - 1].wealth];

  return (
    <AutoSizer>
      {({ width, height }: { width: number; height: number }) => (
        <AreaChart
          data={data}
          width={width}
          height={height}
          margin={{
            top: 20,
            right: 30,
            left: 5,
            bottom: 20,
          }}
        >
          <XAxis
            dataKey="name"
            tickFormatter={formatXAxisTick}
            ticks={xAxisTicks}
            interval={0}
            label={{
              value: `${t('cashbackCalculator.detailed.cashbackAreaChart.depotVolume')}`,
              position: 'insideRight',
              offset: isMobile ? 60 : 100,
              style: { fontWeight: 'bold' },
            }}
          />
          <YAxis
            width={Y_AXIS_WIDTH}
            tickFormatter={formatYAxisTick}
            mirror
            padding={{ top: 40, bottom: 0 }}
            label={{
              value: `${t('cashbackCalculator.detailed.cashbackAreaChart.portfolioCommission')}`,
              angle: -0,
              position: 'insideTopLeft',
              offset: 5,
              style: { fontWeight: 'bold' },
            }}
          />
          <Area
            type="monotone"
            fillOpacity="1"
            dataKey="fee"
            stackId="1"
            animationDuration={1000}
            stroke={theme.palette.button.cashbackButton.default}
            fill={theme.palette.button.cashbackButton.default}
            label={(props: any) =>
              model === '80-percent' ? (
                <CashbackLabel
                  {...props}
                  id="rentablo-part-label"
                  maxIndex={data.length - 1}
                  height={height}
                  label={t('cashbackCalculator.detailed.cashbackAreaChart.rentabloFeeCB50')}
                  style={{
                    fill: theme.palette.primary.main,
                    fontWeight: 'bold',
                    fontSize: '19px',
                  }}
                />
              ) : null
            }
          />
          <Area
            type="monotone"
            fillOpacity="1"
            dataKey="cashback"
            stackId="1"
            animationDuration={1000}
            stroke={theme.palette.text.primary}
            fill={theme.palette.text.primary}
            label={(props: any) => (
              <CashbackLabel
                {...props}
                id="your-part-label"
                maxIndex={data.length - 1}
                height={height}
                label={t('cashbackCalculator.detailed.cashbackAreaChart.cashbackLabel')}
                style={{
                  fill: theme.palette.text.primary,
                  fontWeight: 'bold',
                  fontSize: '19px',
                }}
              />
            )}
          />
          {wealth <= HIDE_CASHBACK_THRESHOLD_AT_WEALTH && model === 'flat-fee' && (
            <ReferenceLine
              x={cashback100Threshold}
              stroke={theme.palette.button.cashbackButton.pressed}
              strokeDasharray="5 5"
              label={(props: any) => <CashbackTypeLabel {...props} height={height} />}
            />
          )}
          {model === 'flat-fee' && (
            <ReferenceLine
              y={CB_100_FEE + (data[data.length - 1].cashback + data[data.length - 1].fee) * 0.05}
              strokeWidth={0}
            >
              <Label
                style={{
                  fill: theme.palette.button.cashbackButton.default,
                  fontWeight: 'bold',
                  fontSize: '19px',
                }}
                content={(props: any) => (
                  <RentabloFeeLabel {...props} label={rentabloFeeLabel(cashback)} />
                )}
              ></Label>
            </ReferenceLine>
          )}
          <ReferenceDot
            x={Math.ceil(wealth / dataPointInterval) * dataPointInterval}
            y={wealth * portfolioCommissionPercent}
            r={20}
            fill="red"
            stroke="none"
            shape={(props: any) => (
              <CashbackIndicator {...props} label={toEuroWholeNumber(cashback)} height={height} />
            )}
          />
          <Tooltip
            contentStyle={{ borderRadius: theme.shape.borderRadius }}
            formatter={formatTooltip}
            labelFormatter={formatTooltipLabel}
            labelStyle={{ fontWeight: 'bold' }}
            itemSorter={(payload: any) => {
              return payload.dataKey === 'fee' ? 1 : 0;
            }}
          />
          <use id="use" xlinkHref="#rentablo-part-label" />
        </AreaChart>
      )}
    </AutoSizer>
  );
};
